/**
 * Utilidades para compresión y optimización de imágenes
 * Convierte imágenes a WebP con máxima compresión manteniendo calidad aceptable
 */
class ImageCompressionUtils {
  static CONFIG = {
    MAX_WIDTH: 800,
    MAX_HEIGHT: 600,
    QUALITY: 0.85,
    FORMAT: 'webp',
    THUMBNAIL_MAX_SIZE: 200,
    THUMBNAIL_QUALITY: 0.7
  };

  /**
   * Comprimir un archivo de imagen
   */
  static async compressImageFile(file, options = {}) {
    const config = { ...ImageCompressionUtils.CONFIG, ...options };
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.onload = () => {
        try {
          // Calcular nuevas dimensiones manteniendo proporción
          const { width, height } = ImageCompressionUtils.calculateDimensions(
            img.width, 
            img.height, 
            config.MAX_WIDTH, 
            config.MAX_HEIGHT
          );
          
          // Configurar canvas
          canvas.width = width;
          canvas.height = height;
          
          // Dibujar imagen redimensionada
          ctx.drawImage(img, 0, 0, width, height);
          
          // Convertir a blob comprimido
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Error al comprimir imagen'));
              }
            },
            `image/${config.FORMAT}`,
            config.QUALITY
          );
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => reject(new Error('Error al cargar imagen'));
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Comprimir imagen desde URL
   */
  static async compressImageFromUrl(imageUrl, options = {}) {
    const config = { ...ImageCompressionUtils.CONFIG, ...options };
    
    return new Promise((resolve, reject) => {
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        try {
          // Calcular nuevas dimensiones
          const { width, height } = ImageCompressionUtils.calculateDimensions(
            img.width, 
            img.height, 
            config.MAX_WIDTH, 
            config.MAX_HEIGHT
          );
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          
          canvas.toBlob(
            (blob) => {
              if (blob) {
                resolve(blob);
              } else {
                reject(new Error('Error al comprimir imagen desde URL'));
              }
            },
            `image/${config.FORMAT}`,
            config.QUALITY
          );
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => reject(new Error('Error al cargar imagen desde URL'));
      img.src = imageUrl;
    });
  }

  /**
   * Crear thumbnail de imagen
   */
  static async createThumbnail(file, options = {}) {
    const config = {
      MAX_WIDTH: ImageCompressionUtils.CONFIG.THUMBNAIL_MAX_SIZE,
      MAX_HEIGHT: ImageCompressionUtils.CONFIG.THUMBNAIL_MAX_SIZE,
      QUALITY: ImageCompressionUtils.CONFIG.THUMBNAIL_QUALITY,
      FORMAT: ImageCompressionUtils.CONFIG.FORMAT,
      ...options
    };
    
    return ImageCompressionUtils.compressImageFile(file, config);
  }

  /**
   * Calcular dimensiones manteniendo proporción
   */
  static calculateDimensions(originalWidth, originalHeight, maxWidth, maxHeight) {
    let { width, height } = { width: originalWidth, height: originalHeight };
    
    // Si la imagen es más grande que los límites, redimensionar
    if (width > maxWidth || height > maxHeight) {
      const aspectRatio = width / height;
      
      if (width > height) {
        width = maxWidth;
        height = width / aspectRatio;
        
        if (height > maxHeight) {
          height = maxHeight;
          width = height * aspectRatio;
        }
      } else {
        height = maxHeight;
        width = height * aspectRatio;
        
        if (width > maxWidth) {
          width = maxWidth;
          height = width / aspectRatio;
        }
      }
    }
    
    return { 
      width: Math.round(width), 
      height: Math.round(height) 
    };
  }

  /**
   * Obtener información de un archivo de imagen
   */
  static async getImageInfo(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      img.onload = () => {
        resolve({
          width: img.width,
          height: img.height,
          size: file.size,
          type: file.type,
          name: file.name
        });
      };
      
      img.onerror = () => reject(new Error('Error al obtener información de imagen'));
      img.src = URL.createObjectURL(file);
    });
  }

  /**
   * Validar si un archivo es una imagen válida
   */
  static isValidImageFile(file) {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif'];
    const maxSize = 10 * 1024 * 1024; // 10MB
    
    return {
      isValid: validTypes.includes(file.type) && file.size <= maxSize,
      errors: [
        !validTypes.includes(file.type) && 'Formato de imagen no válido',
        file.size > maxSize && 'Imagen demasiado grande (máximo 10MB)'
      ].filter(Boolean)
    };
  }

  /**
   * Procesar imagen completa (compresión + thumbnail)
   */
  static async processImage(file, options = {}) {
    const validation = ImageCompressionUtils.isValidImageFile(file);
    
    if (!validation.isValid) {
      throw new Error(validation.errors.join(', '));
    }

    const originalInfo = await ImageCompressionUtils.getImageInfo(file);
    
    // Comprimir imagen principal
    const compressedBlob = await ImageCompressionUtils.compressImageFile(file, options);
    
    // Crear thumbnail
    const thumbnailBlob = await ImageCompressionUtils.createThumbnail(file, options.thumbnail);
    
    return {
      original: {
        info: originalInfo,
        file: file
      },
      compressed: {
        blob: compressedBlob,
        size: compressedBlob.size,
        reduction: ((file.size - compressedBlob.size) / file.size * 100).toFixed(1)
      },
      thumbnail: {
        blob: thumbnailBlob,
        size: thumbnailBlob.size
      }
    };
  }

  /**
   * Convertir blob a File con nombre personalizado
   */
  static blobToFile(blob, fileName, mimeType) {
    return new File([blob], fileName, { 
      type: mimeType || blob.type,
      lastModified: Date.now()
    });
  }

  /**
   * Generar nombre optimizado para archivo
   */
  static generateOptimizedFileName(originalName, isCompressed = true, isThumbnail = false) {
    const timestamp = Date.now();
    const baseName = originalName.replace(/\.[^/.]+$/, ""); // Quitar extensión
    const suffix = isThumbnail ? '_thumb' : (isCompressed ? '_opt' : '');
    
    return `${timestamp}_${baseName}${suffix}.webp`;
  }
}

export default ImageCompressionUtils;